import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, Divider, IconButton, LinearProgress, Step, StepLabel, Stepper, Typography } from '@mui/material';
import useTranslation from 'hooks/useTranslation/useTranslation';
import * as React from 'react';
import wizardImg from 'v2/assets/wizard.png';
import useModalContext from 'v2/components/Modal/useModalContext';
import { WizardStep, WizardStepContext } from './WizardModalStepContext';

export type { WizardStep };

interface WizardModalTemplateProps {
  title: string;
  steps: WizardStep[];
  children: React.ReactNode;
  id?: string;
  initialStep?: number;
  hideCloseButton?: boolean;
  /**
   * Called when the last step's default footer "Finish" button is clicked.
   * Only used when a step does not register its own footer content.
   */
  onFinish?: () => void;
}

const WizardModalTemplate: React.FC<WizardModalTemplateProps> = ({
  title,
  steps,
  children,
  onFinish,
  id,
  initialStep = 0,
  hideCloseButton = false
}) => {
  const [activeStep, setActiveStep] = React.useState(initialStep);
  const [footerContents, setFooterContents] = React.useState<Record<number, React.ReactNode>>({});
  const { closeModal } = useModalContext();
  const { translation } = useTranslation();

  const totalSteps = steps.length;
  const isFirstStep = activeStep === 0;
  const isLastStep = activeStep === totalSteps - 1;

  const goToNext = React.useCallback(() => {
    if (isLastStep) {
      onFinish?.();
    } else {
      setActiveStep((s) => s + 1);
    }
  }, [isLastStep, onFinish]);

  const goToPrev = React.useCallback(() => {
    setActiveStep((s) => Math.max(s - 1, 0));
  }, []);

  const setFooterContent = React.useCallback((stepIndex: number, content: React.ReactNode) => {
    setFooterContents((prev) => ({ ...prev, [stepIndex]: content }));
  }, []);

  const contextValue = React.useMemo(
    () => ({ activeStep, totalSteps, steps, goToNext, goToPrev, isFirstStep, isLastStep, setFooterContent }),
    [activeStep, totalSteps, steps, goToNext, goToPrev, isFirstStep, isLastStep, setFooterContent]
  );

  const [easterEggVisible, setEasterEggVisible] = React.useState(false);
  const konamiSequence = React.useRef<string[]>([]);
  const konamiCode = [
    'ArrowUp',
    'ArrowUp',
    'ArrowDown',
    'ArrowDown',
    'ArrowLeft',
    'ArrowRight',
    'ArrowLeft',
    'ArrowRight',
    'n',
    'b'
  ];

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      konamiSequence.current = [...konamiSequence.current, event.key].slice(-konamiCode.length);
      if (konamiSequence.current.join(',') === konamiCode.join(',')) {
        setEasterEggVisible((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const stepChildren = React.Children.toArray(children);
  const progress = ((activeStep + 1) / totalSteps) * 100;

  const defaultFooter = (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column-reverse', md: 'row' }, gap: 2 }}>
      {!isFirstStep && (
        <Button id="wizard-modal-back-button" variant="outlined" onClick={goToPrev}>
          {translation.back}
        </Button>
      )}
      <Button
        id="wizard-modal-next-button"
        variant="contained"
        endIcon={!isLastStep ? <ChevronRightIcon /> : undefined}
        onClick={goToNext}
      >
        {isLastStep ? steps[activeStep].label : steps[activeStep + 1].label}
      </Button>
    </Box>
  );

  return (
    <WizardStepContext.Provider value={contextValue}>
      <Box
        id={id}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          borderRadius: 2,
          overflow: 'hidden',
          boxShadow: (theme) => theme.shadows[4],
          background: (theme) => theme.custom.background.level2
        }}
      >
        {easterEggVisible && (
          <Box
            component="img"
            src={wizardImg}
            alt="wizard"
            sx={{
              position: 'absolute',
              bottom: 72,
              right: 16,
              width: 66,
              height: 66,
              borderRadius: '50%',
              objectFit: 'cover',
              zIndex: 10,
              pointerEvents: 'none',
              border: (theme) => `2px solid ${theme.palette.divider}`,
              boxShadow: (theme) => theme.shadows[4]
            }}
          />
        )}

        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            py: 2,
            flexShrink: 0
          }}
        >
          <Typography variant="h5" color="textPrimary">
            {title}
          </Typography>
          {!hideCloseButton && (
            <IconButton id="wizard-modal-close-button" onClick={closeModal} aria-label={translation.close} edge="end">
              <CloseIcon />
            </IconButton>
          )}
        </Box>
        <Divider />

        {/* Body */}
        <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
          {/* Sidebar — desktop only */}
          <Box
            sx={(theme) => ({
              display: { xs: 'none', md: 'flex' },
              flexDirection: 'column',
              width: 300,
              flexShrink: 0,
              background: theme.custom.background.level1,
              borderRight: 1,
              borderColor: theme.palette.divider,
              pt: 3,
              pb: 2,
              px: 2
            })}
          >
            <Stepper activeStep={activeStep} orientation="vertical">
              {steps.map((step) => (
                <Step key={step.label}>
                  <StepLabel
                    optional={
                      step.description ? (
                        <Typography variant="caption" color="textPrimary">
                          {step.description}
                        </Typography>
                      ) : undefined
                    }
                  >
                    {step.label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>

          {/* Content area */}
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, overflowY: 'auto', p: 3 }}>
            {/* Mobile progress indicator */}
            <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 1.5, mb: 3 }}>
              <Typography variant="caption" color="textSecondary" sx={{ whiteSpace: 'nowrap' }}>
                {activeStep + 1} / {totalSteps}
              </Typography>
              <LinearProgress variant="determinate" value={progress} sx={{ flex: 1, borderRadius: 1 }} />
              <Typography variant="caption" color="textSecondary" sx={{ whiteSpace: 'nowrap' }}>
                {steps[activeStep].label}
              </Typography>
            </Box>

            {/* Render all steps but only show the active one, preserving component state */}
            {stepChildren.map((child, index) => (
              <Box
                key={index}
                sx={{
                  flexDirection: 'column',
                  flex: 1,
                  minHeight: 0,
                  display: index === activeStep ? 'flex' : 'none'
                }}
              >
                {child}
              </Box>
            ))}
          </Box>
        </Box>

        <Divider />

        {/* Footer */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column-reverse', md: 'row' },
            alignItems: { xs: 'stretch', md: 'center' },
            justifyContent: { md: hideCloseButton ? 'end' : 'space-between' },
            gap: 2,
            px: 3,
            py: 1.5,
            flexShrink: 0
          }}
        >
          {!hideCloseButton && (
            <Button
              id="wizard-modal-cancel-button"
              variant="text"
              onClick={closeModal}
              sx={{ display: { xs: 'none', md: 'inline-flex' } }}
            >
              {translation.cancel}
            </Button>
          )}
          {footerContents[activeStep] ?? defaultFooter}
        </Box>
      </Box>
    </WizardStepContext.Provider>
  );
};

export default WizardModalTemplate;
